import React, { type Ref } from 'react'
import clsx from 'clsx'
import { useFormField } from '@hooks/useFormField'
import type { BaseFormFieldProps } from './TextInput'

export type TextareaProps = BaseFormFieldProps & {
  rows?: number
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>
  ref?: Ref<HTMLTextAreaElement>
}

export const Textarea = (props: TextareaProps) => {
  const { name, label, required, errors, dataCompId, rows = 3, ref, ...textareaProps } = props

  const { fieldError, errorId, ariaDescribedBy, requiredText, ariaInvalid } = useFormField(name, errors, required)

  return (
    <div
      className="tw:flex tw:flex-col tw:justify-center tw:min-w-0 tw:w-full"
      {...(dataCompId && { 'data-compid': dataCompId })}
    >
      <label
        htmlFor={name}
        className={clsx('tw:mb-0 tw:pb-2 tw:text-sm tw:text-gray-700 tw:font-medium', fieldError && 'tw:text-red-600')}
      >
        {label} {requiredText}
      </label>

      <textarea
        id={name}
        name={name}
        aria-required={required}
        rows={rows}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        style={{
          resize: 'vertical',
        }}
        className={clsx(
          'resizeable tw:font-normal tw:text-base tw:px-3 tw:py-2 tw:w-full',
          'tw:bg-white tw:border tw:rounded tw:border-gray-400',
          'tw:focus:outline-hidden tw:focus:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100 tw:focus:ring-offset-0',
          'tw:placeholder:text-gray-500 tw:min-h-[80px]',
          fieldError && 'tw:border-red-400 tw:focus:border-red-500 tw:focus:ring-red-200',
          textareaProps.disabled && 'tw:bg-gray-100 tw:cursor-not-allowed tw:opacity-60'
        )}
        ref={ref}
        {...textareaProps}
      />

      <div role="status" aria-relevant="all">
        {fieldError && (
          <p id={errorId} className="tw:text-red-600 tw:font-light tw:text-sm tw:pt-1">
            {fieldError.message}
          </p>
        )}
      </div>
    </div>
  )
}
